import clsx from 'clsx';
import { type ReactNode } from 'react';
import { forwardRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { PlayIcon, RefreshIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../buttons/button/Button.js';
import { ProgressCircle } from '../../../content/progress/ProgressCircle.js';
import { useAriaLabelingProps } from '../../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import type { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { MaskingProps } from '../../../core/types/masking-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import * as styles from '../FilterComposition.sty.js';
import { useFilterComposition } from '../store/useFilterComposition.js';
import { useFilterCompositionState } from '../store/useFilterCompositionState.js';
import { filterCompositionApplyButtonMessages as messages } from './messages.js';

const DISPLAY_NAME = 'FilterComposition.ApplyButton';

/**
 * Accepted properties for FilterComposition.ApplyButton.
 * @public
 */
export interface FilterCompositionApplyButtonProps
  extends
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * Label for the fetch state.
   * @defaultValue "Fetch"
   */
  fetchLabel?: ReactNode;
  /**
   * Label for the apply state.
   * @defaultValue "Apply"
   */
  applyLabel?: ReactNode;
}

/**
 * Apply button slot for use inside FilterComposition.
 *
 * Renders a state-driven button whose variant, icon, and label are managed
 * automatically by the composition. The enabled state is derived by the
 * composition from validation, dirty, and pending-query state; consumers cannot
 * override it. The button state is derived from the FilterComposition store.
 *
 * Renders nothing when it is not inside a `<FilterComposition>`.
 *
 * @public
 */
export const FilterCompositionApplyButton = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  FilterCompositionApplyButtonProps
>(
  (
    {
      fetchLabel = <FormattedMessage {...messages.fetch} />,
      applyLabel = <FormattedMessage {...messages.apply} />,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    },
    ref,
  ) => {
    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const store = useFilterComposition();
    const filterState = useFilterCompositionState(() =>
      store?._getApplyState(),
    );

    // Every hook above stays unconditional so hook order is stable; an
    // unresolved store then renders nothing rather than a broken button.
    if (store === undefined || filterState === undefined) {
      return null;
    }

    const isRefreshOrLoading =
      filterState === 'refresh' || filterState === 'loading';

    const variant = isRefreshOrLoading ? 'emphasized' : 'accent';
    const color = isRefreshOrLoading ? 'neutral' : 'primary';

    let icon = <PlayIcon />;
    if (filterState === 'loading') {
      icon = <ProgressCircle size="small" color="neutral" aria-hidden />;
    } else if (filterState === 'refresh') {
      icon = <RefreshIcon />;
    }

    const labelByState = {
      fetch: fetchLabel,
      apply: applyLabel,
      refresh: <FormattedMessage {...messages.refresh} />,
      loading: <FormattedMessage {...messages.cancel} />,
    };
    const label = labelByState[filterState];

    return (
      <Button
        ref={ref}
        variant={variant}
        color={color}
        onClick={() => {
          if (filterState === 'loading') {
            store.cancel();
          } else {
            // A failing apply already surfaces through the composition's error
            // state, so the rejection carries no extra user-facing information.
            store.apply().catch(() => undefined);
          }
        }}
        className={clsx(
          styles.FilterCompositionApplyButtonSlot,
          consumerClassName,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        data-dt-component={DISPLAY_NAME}
      >
        <Button.Prefix>{icon}</Button.Prefix>
        <Button.Label>{label}</Button.Label>
      </Button>
    );
  },
);

(
  FilterCompositionApplyButton as typeof FilterCompositionApplyButton & {
    displayName: string;
  }
).displayName = DISPLAY_NAME;
